import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';
import { useStory } from '../../context/StoryContext';
import { suggestQuest } from '../../services/aiService';
import { useAuth } from '../../context/AuthContext';

const QuestManager = () => {
  const { state, addQuest, completeQuest } = useStory();
  const { user } = useAuth();
  const [showAdd, setShowAdd] = useState(false);
  const [isSuggesting, setIsSuggesting] = useState(false);
  const [newQuest, setNewQuest] = useState({ title: '', description: '', reward: '', difficulty: 'Easy', type: 'Side', scope: 'Group' });

  const handleSuggest = async () => {
    setIsSuggesting(true);
    try {
      const recentEntries = state.story.slice(-10); // Get last 10 turns for context
      const apiKey = import.meta.env.VITE_OPENROUTER_API_KEY || state.ai.apiKey;
      const suggestion = await suggestQuest(recentEntries, state.world, apiKey, state.ai.model);

      setNewQuest(prev => ({ ...prev, ...suggestion }));
      setShowAdd(true);
    } catch (e) {
      console.error("Failed to suggest quest:", e);
    } finally {
      setIsSuggesting(false);
    }
  };

  const handleAdd = (e) => {
    e.preventDefault();
    addQuest(newQuest);
    setShowAdd(false);
    setNewQuest({ title: '', description: '', reward: '', difficulty: 'Easy', type: 'Side', scope: 'Group' });
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h4 className="text-[10px] font-black text-gray-900 dark:text-purple-400 uppercase tracking-widest">Active Objectives</h4>
        <div className="flex space-x-2">
          <button onClick={handleSuggest} disabled={isSuggesting} className="p-1.5 bg-gray-500/10 text-gray-800 dark:text-purple-400 rounded-lg hover:scale-110 transition-transform disabled:opacity-50" title="Suggest Quest from Narrative">
            <SafeIcon icon={isSuggesting ? FiIcons.FiLoader : FiIcons.FiCpu} className={isSuggesting ? 'animate-spin' : ''} />
          </button>
          <button onClick={() => setShowAdd(true)} className="p-1.5 bg-amber-500/10 text-amber-600 rounded-lg hover:scale-110 transition-transform">
            <SafeIcon icon={FiIcons.FiPlus} />
          </button>
        </div>
      </div>

      <AnimatePresence>
        {showAdd && (
          <motion.form 
            initial={{ height: 0, opacity: 0 }} animate={{ height: 'auto', opacity: 1 }} exit={{ height: 0, opacity: 0 }}
            onSubmit={handleAdd} className="bg-white dark:bg-purple-900/40 p-4 rounded-2xl border border-amber-200 dark:border-amber-900/30 space-y-3 overflow-hidden shadow-sm"
          >
            <input 
              className="input-field text-xs py-2" placeholder="Quest Title" 
              value={newQuest.title} onChange={e => setNewQuest({...newQuest, title: e.target.value})} required 
            />
            <textarea 
              className="input-field text-xs py-2 min-h-[60px]" placeholder="Brief description..." 
              value={newQuest.description} onChange={e => setNewQuest({...newQuest, description: e.target.value})} required 
            />
            <div className="grid grid-cols-3 gap-2">
              <select className="input-field text-[10px] py-1.5" value={newQuest.type} onChange={e => setNewQuest({...newQuest, type: e.target.value})}>
                <option>Main</option><option>Side</option><option>Hidden</option>
              </select>
              <select className="input-field text-[10px] py-1.5" value={newQuest.difficulty} onChange={e => setNewQuest({...newQuest, difficulty: e.target.value})}>
                <option>Easy</option><option>Medium</option><option>Hard</option>
              </select>
              <select className="input-field text-[10px] py-1.5" value={newQuest.scope || 'Group'} onChange={e => setNewQuest({...newQuest, scope: e.target.value})}>
                <option>Group</option><option>Individual</option>
              </select>
            </div>
            <input
              className="input-field text-xs py-2" placeholder="Reward (e.g., 50 XP, 10 Gold)"
              value={newQuest.reward} onChange={e => setNewQuest({...newQuest, reward: e.target.value})}
            />
            <button type="submit" className="w-full btn-primary text-[10px] py-2 text-gray-900 dark:text-white">Forge Quest</button>
          </motion.form>
        )}
      </AnimatePresence>

      <div className="space-y-3">
        {(state.quests || []).filter(q => !q.user_id || q.user_id === user?.id || state.ownerId === user?.id).map((q) => (
          <motion.div 
            layout key={q.id} 
            className={`group p-4 rounded-xl border transition-all ${q.status === 'Completed' ? 'bg-gray-50/50 dark:bg-purple-900/10 border-gray-100 dark:border-purple-900/40 opacity-50' : 'bg-white dark:bg-purple-900 border-gray-100 dark:border-purple-800 shadow-sm'}`}
          >
            <div className="flex justify-between items-start mb-2">
              <div className="flex items-center space-x-2">
                <div className={`w-1.5 h-1.5 rounded-full ${q.type === 'Main' ? 'bg-amber-500' : 'bg-purple-400'}`} />
                <h5 className={`text-xs font-bold ${q.status === 'Completed' ? 'line-through' : 'text-purple-950 dark:text-white'}`}>{q.title}</h5>
              </div>
              {q.status !== 'Completed' && (
                <button onClick={() => completeQuest(q.id)} className="text-gray-900 hover:text-green-500 transition-colors dark:text-purple-300">
                  <SafeIcon icon={FiIcons.FiCheckCircle} />
                </button>
              )}
            </div>
            <p className="text-[10px] text-gray-900 dark:text-purple-400 leading-relaxed mb-2 line-clamp-2 group-hover:line-clamp-none transition-all">{q.description}</p>
            {q.reward && (
              <div className="text-[10px] text-amber-600 font-bold mb-3 flex items-center gap-1">
                <SafeIcon icon={FiIcons.FiGift} className="text-[10px]" /> {q.reward}
              </div>
            )}
            <div className="flex justify-between items-center text-[9px] font-black uppercase tracking-tighter">
              <span className="text-amber-600">{q.difficulty}</span>
              <span className="text-gray-900 dark:text-purple-400">{q.type} Quest {q.user_id ? '(Individual)' : '(Group)'}</span>
            </div>
          </motion.div>
        ))}
      </div>
    </div>
  );
};

export default QuestManager;